import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { formatDate } from '@/utils/format';

import type { AnalystReportRecord, AnalystStats } from '@repo/shared-types';

interface jsPDFWithAutoTable extends jsPDF {
  lastAutoTable: { finalY: number };
}

const INK: [number, number, number] = [22, 24, 29];
const MUTED = 120;

export function generateAnalystReportPDF(report: AnalystReportRecord) {
  const doc = new jsPDF() as jsPDFWithAutoTable;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  let y = 20;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 18) {
      doc.addPage();
      y = 20;
    }
  };

  const heading = (text: string, size = 13) => {
    ensureSpace(14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    doc.setTextColor(...INK);
    doc.text(text, margin, y);
    y += size * 0.5 + 3;
  };

  const paragraph = (text: string, size = 10, color = 60) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    doc.setTextColor(color);
    const lines = doc.splitTextToSize(text, contentWidth);
    for (const line of lines) {
      ensureSpace(size * 0.5 + 1);
      doc.text(line, margin, y);
      y += size * 0.5 + 1.5;
    }
    y += 2;
  };

  const table = (head: string[][], body: (string | number)[][]) => {
    autoTable(doc, {
      startY: y,
      head,
      body: body.map((r) => r.map((c) => String(c))),
      theme: 'striped',
      headStyles: { fillColor: INK, halign: 'left' },
      styles: { fontSize: 9, cellPadding: 2 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 8;
  };

  // ---- Title block --------------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...INK);
  const titleLines = doc.splitTextToSize(report.title, contentWidth);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 8 + 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(MUTED);
  const meta = [
    report.llm_provider
      ? `Generated by ${report.llm_provider}${report.llm_model ? ` (${report.llm_model})` : ''}`
      : null,
    `Created ${formatDate(report.created_at)}`,
    report.params?.sample_size ? `${report.params.sample_size} transcripts analysed` : null,
  ]
    .filter(Boolean)
    .join('  •  ');
  doc.text(meta, margin, y);
  y += 8;

  const narrative = report.narrative;
  const stats = report.stats;

  if (narrative) {
    // Headline
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...INK);
    const hl = doc.splitTextToSize(narrative.headline, contentWidth);
    ensureSpace(hl.length * 6 + 4);
    doc.text(hl, margin, y);
    y += hl.length * 6 + 4;

    heading('Executive Summary');
    for (const para of narrative.executive_summary) paragraph(para);
  }

  // ---- Stats --------------------------------------------------------------
  if (stats) {
    heading('The Numbers');
    renderStatsTables(stats, table, paragraph);
  }

  if (narrative) {
    heading('Key Findings');
    narrative.key_findings.forEach((f, i) => {
      paragraph(`${i + 1}. ${f.title}`, 10.5, INK[0]);
      paragraph(f.detail);
    });

    if (narrative.reason_breakdown.length > 0) {
      heading('Why the Failure Group Didn’t Convert');
      table(
        [['Reason', 'Count', 'Share', 'Nature']],
        narrative.reason_breakdown.map((r) => [r.reason, r.count, `${r.share_pct}%`, r.nature]),
      );
    }

    heading('Recommendations');
    [...narrative.recommendations]
      .sort((a, b) => a.priority - b.priority)
      .forEach((r) => {
        paragraph(`Priority ${r.priority} — ${r.title}`, 10.5, INK[0]);
        paragraph(r.detail);
      });

    heading('Bottom Line');
    paragraph(narrative.bottom_line);
  }

  // Footer page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  const safeName = report.title.replace(/[^a-z0-9]/gi, '_');
  doc.save(`${safeName}.pdf`);
}

function renderStatsTables(
  stats: AnalystStats,
  table: (head: string[][], body: (string | number)[][]) => void,
  paragraph: (text: string, size?: number, color?: number) => void,
) {
  paragraph(
    `${stats.success_label}: ${stats.success_count}  •  ${stats.failure_label}: ${stats.failure_count}  •  Total: ${stats.total}  •  Success share: ${stats.success_share_pct}%`,
    10,
    60,
  );

  table(
    [['Channel', `${stats.success_label}`, 'Total', 'Conversion']],
    stats.by_channel.map((r) => [r.label, r.success, r.total, `${r.conversion_pct}%`]),
  );

  table(
    [['Configuration', `${stats.success_label}`, 'Total', 'Conversion']],
    stats.by_configuration.map((r) => [r.label, r.success, r.total, `${r.conversion_pct}%`]),
  );

  if (stats.failure_status_breakdown.length > 0) {
    table(
      [['Failure payment status', 'Count']],
      stats.failure_status_breakdown.map((r) => [r.label, r.count]),
    );
  }
}
